import { create } from 'zustand';
import type { AdminLoginInput, AdminRole } from '@orbit-sync/shared-types';
import { api } from '../lib/api/index.js';
import {
  clearAdminToken,
  getAdminToken,
  setAdminToken,
  subscribeToAdminToken,
} from '../lib/adminTokenStorage.js';

/**
 * The administrator session - entirely separate from `auth/authStore.ts`.
 *
 * The administrator is not a user: signing in here does not sign anyone into
 * chat, and signing out of chat leaves this alone. The token lives in
 * `lib/adminTokenStorage.ts`, for the same one-owner reason the user's does.
 */
interface AdminAuthState {
  status: 'loading' | 'authenticated' | 'unauthenticated';
  username: string | null;
  /** `operator` sees the dashboard and users, and may only enable or disable accounts. */
  role: AdminRole | null;
  pending: boolean;

  bootstrap: () => Promise<void>;
  login: (input: AdminLoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAdminAuthStore = create<AdminAuthState>((set) => ({
  status: 'loading',
  username: null,
  role: null,
  pending: false,

  async bootstrap(): Promise<void> {
    if (!getAdminToken()) {
      set({ status: 'unauthenticated', username: null, role: null });
      return;
    }

    try {
      const me = await api.admin.me();
      set({ status: 'authenticated', username: me.username, role: me.role });
    } catch {
      // Expired, revoked, or the server restarted (sessions are in memory).
      clearAdminToken();
      set({ status: 'unauthenticated', username: null, role: null });
    }
  },

  async login(input: AdminLoginInput): Promise<void> {
    set({ pending: true });

    try {
      const session = await api.admin.login(input);
      setAdminToken(session.token);
      set({
        status: 'authenticated',
        username: session.username,
        role: session.role,
        pending: false,
      });
    } catch (error) {
      set({ pending: false });
      throw error;
    }
  },

  async logout(): Promise<void> {
    // A real revocation on the server, unlike a user sign-out; but the local
    // session ends whether or not that request makes it.
    await api.admin.logout().catch(() => undefined);
    clearAdminToken();
    set({ status: 'unauthenticated', username: null, role: null });
  },
}));

// A 401 from any admin call clears the token in the API client; reflect it.
subscribeToAdminToken((token) => {
  if (token === null && useAdminAuthStore.getState().status === 'authenticated') {
    useAdminAuthStore.setState({ status: 'unauthenticated', username: null, role: null });
  }
});
